import {FC, useState} from 'react'
import {searchAccountsByCity, CitySearchAccount} from './_requests'

const OCCUPATION_LABEL: Record<string, string> = {
  real_estate: 'נדל״ן',
  cars: 'רכב',
  hr: 'כח אדם',
  other: 'אחר',
}

const AccountCitySearchPage: FC = () => {
  const [city, setCity] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [accounts, setAccounts] = useState<CitySearchAccount[]>([])
  const [message, setMessage] = useState('')

  const run = async () => {
    const q = city.trim()
    if (q.length < 2) {
      setMessage('הקלד לפחות 2 תווים')
      return
    }
    setLoading(true)
    setMessage('')
    try {
      const res = await searchAccountsByCity(q, activeOnly)
      setAccounts(res.data.accounts || [])
      setMessage(res.data.message || '')
      setSearched(true)
    } catch (e: any) {
      setMessage(e?.response?.data?.error || 'שגיאה בחיפוש')
      setAccounts([])
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div dir='rtl' style={{maxWidth: 1080, margin: '0 auto', padding: '28px 20px'}}>
      <h1 style={{fontSize: 22, fontWeight: 800, color: '#1b1f2a', marginBottom: 4}}>חיפוש לקוחות לפי עיר</h1>
      <p style={{fontSize: 13, color: '#8a92a6', marginBottom: 20}}>
        חיפוש חשבונות שהעיר מופיעה בשם הקמפיין או במילות החיפוש של ספריית הקבוצות — כדי לאתר במהירות את לקוחות הנדל״ן באזור.
      </p>

      <div className='d-flex flex-wrap align-items-center gap-3' style={{marginBottom: 20}}>
        <input
          autoFocus
          type='text'
          placeholder='שם עיר, למשל: נס ציונה'
          value={city}
          onChange={(e) => setCity(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') run()
          }}
          style={{
            flex: '1 1 280px', maxWidth: 360, padding: '10px 14px', fontSize: 14,
            border: '1px solid #e6e8ee', borderRadius: 10,
          }}
        />
        <button
          onClick={run}
          disabled={loading}
          style={{
            padding: '10px 20px', fontSize: 14, fontWeight: 700, color: '#fff',
            background: '#4f46e5', border: 'none', borderRadius: 10, cursor: 'pointer',
          }}
        >
          {loading ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='bi bi-search me-1'></i>}
          חפש
        </button>
        <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#3a4151', cursor: 'pointer'}}>
          <input type='checkbox' checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          רק חשבונות פעילים
        </label>
      </div>

      {message && <div style={{fontSize: 13, color: '#b45309', marginBottom: 14}}>{message}</div>}

      {searched && !loading && (
        <div style={{fontSize: 13, color: '#8a92a6', marginBottom: 10}}>
          נמצאו {accounts.length} חשבונות
        </div>
      )}

      {accounts.length > 0 && (
        <div style={{border: '1px solid #eef0f4', borderRadius: 12, overflow: 'hidden'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
            <thead>
              <tr style={{background: '#f8f9fb', color: '#8a92a6', textAlign: 'right'}}>
                <th style={{padding: '10px 14px', fontWeight: 600}}>שם</th>
                <th style={{padding: '10px 14px', fontWeight: 600}}>חברה</th>
                <th style={{padding: '10px 14px', fontWeight: 600}}>קשר</th>
                <th style={{padding: '10px 14px', fontWeight: 600}}>תחום</th>
                <th style={{padding: '10px 14px', fontWeight: 600}}>סטטוס</th>
                <th style={{padding: '10px 14px', fontWeight: 600}}>נמצא ב</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a._id} style={{borderTop: '1px solid #f1f2f5'}}>
                  <td style={{padding: '10px 14px', fontWeight: 600, color: '#1b1f2a'}}>{a.name || '—'}</td>
                  <td style={{padding: '10px 14px', color: '#3a4151'}}>{a.company_name || '—'}</td>
                  <td style={{padding: '10px 14px', color: '#3a4151'}}>
                    {a.phone && <div>{a.phone}</div>}
                    {a.email && <div style={{color: '#8a92a6', fontSize: 12}}>{a.email}</div>}
                    {!a.phone && !a.email && '—'}
                  </td>
                  <td style={{padding: '10px 14px', color: '#3a4151'}}>
                    {a.library_occupation ? OCCUPATION_LABEL[a.library_occupation] || a.library_occupation : '—'}
                  </td>
                  <td style={{padding: '10px 14px'}}>
                    <span
                      style={{
                        fontSize: 11, fontWeight: 700, borderRadius: 8, padding: '3px 9px',
                        color: a.active ? '#15803d' : '#b91c1c',
                        background: a.active ? '#dcfce7' : '#fee2e2',
                      }}
                    >
                      {a.active ? 'פעיל' : 'לא פעיל'}
                    </span>
                  </td>
                  <td style={{padding: '10px 14px', color: '#6b7280', fontSize: 12}}>
                    {a.matched_campaigns.map((c, i) => (
                      <div key={'c' + i} title='שם קמפיין'>
                        <i className='bi bi-megaphone me-1'></i>
                        {c.name}
                        {!c.enabled && <span style={{color: '#b91c1c'}}> (כבוי)</span>}
                      </div>
                    ))}
                    {a.matched_searches.map((s, i) => (
                      <div key={'s' + i} title='מילת חיפוש בספריה'>
                        <i className='bi bi-search me-1'></i>
                        {s}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default AccountCitySearchPage
